import { MIN_PASSWORD_LENGTH } from '@ofmos/shared/auth';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useAppClient } from '../../data/ClientProvider';
import { Button, FormField, Input, Spinner } from '../../ui';
import { AuthLayout } from './AuthLayout';
import { authErrorMessage } from './authMessages';
import { DEFAULT_BUTTON } from './LoginPage';
import { PASSWORD_ERROR } from './RegisterPage';

/**
 * `/reset-password/:token` (accounts F1, D-F1.4).
 *
 * THE LINK IS THE CREDENTIAL, so the screen asks the server whether it is still worth
 * showing a password field for BEFORE it shows one: a person who pastes a spent link
 * should read why rather than type a password into a form that is going to refuse it.
 *
 * IT DOES NOT SIGN ANYBODY IN, and that is the point rather than an omission. A reset is
 * the remedy for "somebody else may be in my account", so the write ends every session of
 * that account - and handing this browser a new one in the same breath would make the one
 * promise this flow makes false at the moment it is made. The screen says so and offers
 * Sign In.
 *
 * ITS PIECES ARE THE REBUILT SIGN-IN'S (F4, ledger E-W4-46): `AuthLayout`'s window, one kit
 * Lg field with its label above it, the kit's tinted content-area default button
 * (`DEFAULT_BUTTON`, never the glass capsule), `RegisterPage`'s one sentence about a short
 * password, and the way back as a plain 13px link.
 */
export function ResetPasswordPage() {
  const client = useAppClient();
  const { token = '' } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const link = useQuery({
    queryKey: ['password-reset', token],
    /**
     * IT RETURNS A VALUE AND NOT `void`. TanStack Query refuses `undefined` as query data
     * ("Query data cannot be undefined") and reports it as an ERROR, so a `queryFn` that
     * merely resolved made every valid link render the refusal screen - found by walking
     * the fixture build rather than by reading this.
     */
    queryFn: async () => {
      await client.auth.checkPasswordReset(token);
      return 'valid' as const;
    },
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  const submit = (): void => {
    if (tooShort || password === '' || pending) return;
    setPending(true);
    setError(undefined);
    client.auth.setPasswordWithToken(token, password).then(
      () => {
        setPending(false);
        setDone(true);
      },
      (cause: unknown) => {
        setPending(false);
        setError(authErrorMessage(cause));
      },
    );
  };

  if (done) {
    return (
      <AuthLayout
        title="Your password was changed."
        subtitle="Every other session of this account was signed out."
      >
        {/*
          THE SAME DEFAULT BUTTON AS THE FORM'S, because this screen's one action is a
          navigation rather than a submit: `Link` carries the route and the button carries
          the kit's tinted fill, so the completed screen does not change shape.
        */}
        <Link to="/login">
          <Button variant="push" block style={DEFAULT_BUTTON}>
            Sign in
          </Button>
        </Link>
      </AuthLayout>
    );
  }

  if (link.isPending) {
    return (
      <AuthLayout title="Choose a new password" subtitle="Checking the link.">
        <div className="flex justify-center py-4" aria-busy="true">
          <Spinner label="Checking the link" />
        </div>
      </AuthLayout>
    );
  }

  if (link.isError) {
    return (
      <AuthLayout title="This link doesn't work any more." subtitle="Ask for a new one.">
        {/* The server's sentence, which already says which kind of refusal this is. */}
        <p className="text-sm text-ink-muted">{authErrorMessage(link.error)}</p>
        <p className="text-2xs text-ink-subtle">
          <Link to="/forgot-password" className="text-accent">
            Ask for a new link
          </Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      /**
       * WHAT SETTING IT DOES, not what signing in with it does: the sessions end at the
       * WRITE (`setPasswordWithToken`), before anybody signs in with the new password, and
       * this screen deliberately does not sign the person in at all (round 2, finding 5).
       */
      subtitle="Setting it signs this account out everywhere else."
    >
      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormField
          label="New password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          /** R8's own sentence, and the one `RegisterForm` already shows for this rule. */
          error={tooShort ? PASSWORD_ERROR : error}
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={pending}
              invalid={tooShort || error !== undefined}
            />
          )}
        </FormField>

        <Button
          type="submit"
          variant="push"
          block
          style={DEFAULT_BUTTON}
          disabled={password === ''}
          loading={pending}
        >
          Set password
        </Button>
      </form>

      <p className="text-2xs text-ink-subtle">
        <Link to="/login" className="text-accent">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
